/* ============================================================================
   ADDON: client error beacon — SENDER HALF.
   ----------------------------------------------------------------------------
   Pairs with snippets/hub-errlog-boot.liquid, which installs the listeners
   synchronously in <head> and buffers into window.__hubErrQ. This half drains
   that buffer and posts it. Both halves are inert without the
   `client_error_endpoint` theme setting.

   WHY IT EXISTS: before this, a JS failure on a customer's phone was completely
   silent. A grep of the whole theme for window.onerror / addEventListener
   ('error') / Sentry returned nothing. The locker, the quiz, the avatar builder
   and the booking picker are all JS-built — if any of them throws, the visitor
   sees a dead panel and leaves, and we find out never.

   WHAT IT DELIBERATELY DOES NOT SEND
     * No customer id, no name, no email. `member` is a BOOLEAN.
     * No query string, ever — stripped here and stripped again server-side.
       This storefront puts discount codes, booking tokens and (on the quiz)
       email addresses in query strings.
     * No cookie and nothing in localStorage. The session id is a random string
       in sessionStorage that dies with the tab and is joined to nothing.
     * No IP — the server salts and hashes it, and stores only that.

   NOISE CONTROL, because a beacon that cries wolf gets switched off:
     * identical errors are sent ONCE per page view (fingerprint dedupe)
     * at most MAX_PER_PAGE per page view, MAX_PER_SESSION per tab
     * cross-origin scripts collapse to "Script error." with no detail — that is
       the browser, not us. They are sent (the COUNT is signal) but never
       retried, and they are why source/line can be empty.

   It must never be the thing that breaks the page: every path is wrapped, it
   never throws, it never blocks, and a failed send is dropped silently.
   ========================================================================== */
(function () {
  'use strict';

  var body = document.body;
  if (!body) return;

  var EP = body.getAttribute('data-errlog-endpoint');
  if (!EP) return;                                   // not configured => inert

  var MAX_PER_PAGE = 6;
  var MAX_PER_SESSION = 24;
  var SESSION_KEY = 'hub:errlog:n';
  var SID_KEY = 'hub:errlog:sid';

  var sentThisPage = 0;
  var seen = Object.create(null);

  // Sampling knob for the day traffic is big enough that it matters. 1 = send
  // everything, which is right at soft-launch volume — a rare bug that only
  // shows up once is exactly the one worth catching.
  var RATE = parseFloat(body.getAttribute('data-errlog-sample') || '1') || 1;

  function ss(key, val) {
    try {
      if (val === undefined) return window.sessionStorage.getItem(key);
      window.sessionStorage.setItem(key, val);
      return val;
    } catch (_) { return null; }             // Safari private mode throws
  }

  function sid() {
    var s = ss(SID_KEY);
    if (!s) {
      s = (Math.random().toString(36).slice(2) + Date.now().toString(36)).slice(0, 24);
      ss(SID_KEY, s);
    }
    return s;
  }

  function sessionCount(delta) {
    var n = parseInt(ss(SESSION_KEY) || '0', 10) || 0;
    if (delta) { n += delta; ss(SESSION_KEY, String(n)); }
    return n;
  }

  // Path only. Split on both, because a Shopify URL can carry a fragment that
  // itself contains a '?' (the quiz does this).
  function path() {
    try { return String(location.pathname || '/').slice(0, 300); } catch (_) { return '/'; }
  }

  function template() {
    try {
      var m = (body.className || '').match(/template-([a-z0-9_-]+)/i);
      return m ? m[1].slice(0, 60) : null;
    } catch (_) { return null; }
  }

  // The theme's CSS asset carries a ?v= cache-buster that changes on every push,
  // which makes it a free release marker: "was this fixed by that deploy?" is
  // answerable without anyone remembering to bump a version string.
  var RELEASE = (function () {
    try {
      var l = document.querySelector('link[rel=stylesheet][href*="the-hub.css"]');
      var m = l && l.href.match(/[?&]v=([^&]+)/);
      return m ? m[1].slice(0, 60) : null;
    } catch (_) { return null; }
  })();

  function send(e) {
    try {
      if (!e || sentThisPage >= MAX_PER_PAGE) return;
      if (sessionCount() >= MAX_PER_SESSION) return;
      if (RATE < 1 && Math.random() > RATE) return;

      var msg = String(e.message || '').slice(0, 500);
      if (!msg) return;
      var src = String(e.source || '').split('?')[0].slice(0, 300);

      var fp = [e.kind, msg, src, e.line || ''].join('|');
      if (seen[fp]) return;                       // same failure, same page view
      seen[fp] = 1;

      sentThisPage++;
      sessionCount(1);

      var payload = {
        kind: e.kind || 'error',
        message: msg,
        source: src,
        line: e.line != null ? e.line : null,
        col: e.col != null ? e.col : null,
        stack: String(e.stack || '').slice(0, 3000),
        page: path(),
        template: template(),
        member: body.getAttribute('data-member') === 'true',
        vw: window.innerWidth || null,
        vh: window.innerHeight || null,
        release: RELEASE,
        sess: sid()
      };

      var json = JSON.stringify(payload);

      // text/plain keeps this a CORS "simple request". sendBeacon cannot run a
      // preflight, and application/json would force one — so the beacon would
      // silently fail on exactly the unload-time errors it exists to catch.
      // The server reads text() and parses it. Do not "tidy" this to JSON.
      var blob;
      try { blob = new Blob([json], { type: 'text/plain;charset=UTF-8' }); } catch (_) { blob = null; }

      if (blob && navigator.sendBeacon && navigator.sendBeacon(EP, blob)) return;

      if (window.fetch) {
        fetch(EP, {
          method: 'POST',
          body: json,
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
          keepalive: true,      // survives the page going away
          mode: 'cors',
          credentials: 'omit'   // no cookies to an error sink, ever
        })['catch'](function () {});
      }
    } catch (_) { /* a logger that throws is worse than no logger */ }
  }

  function drain() {
    try {
      var Q = window.__hubErrQ;
      if (!Q) return;
      while (Q.length) send(Q.shift());
    } catch (_) {}
  }

  // The boot half calls this on every capture, so an error thrown after load is
  // sent immediately rather than waiting for another event.
  window.__hubErrFlush = drain;
  drain();

  // Anything the boot half missed (it is installed in <head>, so this should be
  // nothing — belt and braces for a page that somehow loaded the asset first).
  window.addEventListener('error', function (ev) {
    if (ev && ev.target && ev.target !== window && ev.target.tagName) return;
    send({
      kind: 'error',
      message: (ev && ev.message) || 'Unknown error',
      source: (ev && ev.filename) || '',
      line: ev && ev.lineno, col: ev && ev.colno,
      stack: (ev && ev.error && ev.error.stack) || ''
    });
  });
})();
